/**
 * WEB-921 — сборка «поверхности» воронки (тема + фон + палитра + шрифт +
 * цвет текста) из settings сущности.
 *
 * Раньше эта сборка была продублирована инлайном в PublicPaywall.tsx (две
 * ветки рендера) и PublicQuiz.tsx. Пока страницу красила та же сущность, что
 * рендерила контент, дубль был безобиден. Теперь встроенный пейволл ОБЪЯВЛЯЕТ
 * свой страничный стиль наверх (funnelPageStyle.ts), и объявление обязано
 * совпадать с тем, что компонент применяет к себе — иначе страница и колонка
 * разъедутся по теме. Поэтому сборка вынесена в одну чистую функцию.
 */

import type { CSSProperties } from 'react';
import {
  getThemeById,
  themeToStyle,
  backgroundToCss,
  paletteToStyle,
  defaultFunnelBaseStyle,
} from '@/themes';
import { resolveFontPair } from '@/themes/fonts';
import { fontSmoothingStyle } from '@/themes/fontSmoothing';
import { getStylePackById } from '@/themes/stylePacks';
import { stylePackToStyle } from '@/themes/stylePackToStyle';
import type { FunnelBackground, FunnelPalette } from '@/store/editorSlice';
import type { Theme } from '@/themes/theme.types';
import { richColorAliasStyle } from '@/types/rich-text';
import { enforceSurfaceTextContrast } from './funnelContrastGuard';

/**
 * WEB-1045: детерминированный фон страницы для палитры без background.
 *
 * Генерация легально отдаёт палитру без background (funnelPaletteSchema:
 * поле optional), а sanitize при палитре вырезает themeId — у такой воронки
 * ни один слой не задавал фон страницы. Превью показывало белый фолбэк
 * PhonePreview (`?? '#fff'`), публика — кабинетный --color-bg-layout
 * (#fafafa, а под тёмной темой ОС — чёрный). Значение фолбэка — ровно тот
 * белый, что фактически виден в редакторе; бэк пишет тот же hex при
 * сохранении палитры (settings-operations.ts / theme-ids.ts, ручной синк).
 */
export const PALETTE_PAGE_BACKGROUND_FALLBACK = '#ffffff';

/**
 * Своё боковое поле экрана у воронки — `settings.screenPaddingX` (задание
 * stylepack/screen-padding, слово владельца 21.10.2026: повторять чужую
 * воронку в точности). Поле экрана на телефоне сейчас задаёт только пак
 * (`density.screenPadXMobile`), а пак общий на много воронок — менять его под
 * одну нельзя.
 *
 * Одна дверь чтения — `readScreenPaddingX`: целое 0–64, иначе «поля нет».
 * Её зовут сборка поверхности ниже (`buildFunnelSurfaceStyle` — плеер и
 * корень предпросмотра: переменные телефонного поля после пака), поля канваса
 * предпросмотра (`PhonePreview.tsx`), запись в редакторе (`editorSlice.ts`) и
 * панель (`StructurePanel.tsx`). Переменные — те же, что ставит пак
 * (`stylePackExtraVars.ts`), и читает их тот же медиазапрос
 * `public-paywall.css`: страница — число, колонка — 0.
 *
 * Почему дверь здесь, а переменные — прямо в сборке, без своей функции и
 * своего модуля: код едет в первом экране плеера, а запас потолка веса по
 * проводу на основе 192ab4785 — 91 байт (сторожа publicEntry.build.web1988 и
 * publicEntryWireWeight.build.web2015; решение владельца 17.10.2026 по
 * WEB-3447 — потолок первого экрана не поднимать). Замер сборкой: свой модуль
 * — +295 байт, дверь здесь со своей функцией переменных — +93, так — +62.
 *
 * Границы — зеркало `SCREEN_PADDING_X_MIN/MAX` схемы сервера
 * (`backend/src/ai-funnels/block-schema/funnel-settings.ts`); совпадение
 * держит сторож `screen-padding-x.ends.spec.ts` сервера.
 */

export const SCREEN_PADDING_X_MIN = 0;
export const SCREEN_PADDING_X_MAX = 64;
/** Переменная поля страницы; поле колонки — она же с `-inner`. */
const PAD_VAR = '--screen-pad-x-mobile';

/** Поле воронки, если оно целое 0–64; иначе undefined («как у стиля»). */
export function readScreenPaddingX(
  settings: { screenPaddingX?: unknown } | null | undefined,
): number | undefined {
  // Number.isInteger отсекает и не-числа: строку, null, NaN, бесконечность.
  const v = settings?.screenPaddingX;
  return Number.isInteger(v) &&
    (v as number) >= SCREEN_PADDING_X_MIN &&
    (v as number) <= SCREEN_PADDING_X_MAX
    ? (v as number)
    : undefined;
}

export interface FunnelSurfaceSettings {
  themeId?: unknown;
  background?: unknown;
  textColor?: unknown;
  palette?: unknown;
  fontId?: unknown;
  /** Стиль-пак — форма воронки (радиусы, плотность, типографская шкала). */
  stylePackId?: unknown;
  /** Своё боковое поле экрана на телефоне, px — сильнее поля пака. */
  screenPaddingX?: unknown;
}

export interface FunnelSurfaceOptions {
  /**
   * Готовая тема вместо резолва по settings.themeId. Нужна конструктору:
   * PhonePreview получает активную тему пропом (ThreePanelEditor резолвит её
   * тем же getThemeById(settings.themeId)), а тесты превью подставляют тему
   * без themeId в settings.
   */
  theme?: Theme;
}

/**
 * Собрать стиль поверхности из settings. Порядок слоёв:
 * тёплая база → тема → фон → ПАЛИТРА → оверрайды воронки (цвет текста) →
 * стиль-пак → шрифт.
 *
 * WEB-942: цвет текста намеренно НАКЛАДЫВАЕТСЯ ПОСЛЕ палитры. Раньше здесь
 * (публичная страница) он шёл ДО палитры и проигрывал ей, а в превью
 * конструктора — после и выигрывал; одни и те же settings давали два разных
 * цвета, и правка «сделай текст белым» была видна в конструкторе, но не на
 * живой воронке. Порядок «оверрайд поверх палитры» — тот же, из которого
 * исходит бэк (settings-operations.ts: applySetPalette снимает textColor,
 * считая его более сильным слоем).
 */
export function buildFunnelSurfaceStyle(
  settings: FunnelSurfaceSettings | null | undefined,
  options?: FunnelSurfaceOptions,
): CSSProperties {
  const themeId = typeof settings?.themeId === 'string' ? settings.themeId : undefined;
  const resolvedTheme = options?.theme ?? (themeId ? getThemeById(themeId) : undefined);
  const bgOverride = settings?.background as FunnelBackground | undefined;
  const palette = settings?.palette as FunnelPalette | undefined;
  const textColor =
    typeof settings?.textColor === 'string' ? settings.textColor : undefined;
  // Своё боковое поле экрана воронки (задание stylepack/screen-padding).
  const ownPadX = readScreenPaddingX(settings);
  const fontId = typeof settings?.fontId === 'string' ? settings.fontId : undefined;
  // Выдуманный id не роняет сборку и не даёт переменных — воронка просто
  // остаётся на прежней (дефолтной) форме.
  const stylePack =
    typeof settings?.stylePackId === 'string'
      ? getStylePackById(settings.stylePackId)
      : undefined;

  const fontStyle: CSSProperties = fontId
    ? ({
        '--font-family-primary': resolveFontPair(fontId).bodyFamily,
        '--font-family-heading': resolveFontPair(fontId).headingFamily,
        // Решение владельца №1: у системного шрифта — сглаживание эталона;
        // у остальных id помощник отдаёт {} (вид прежний).
        ...fontSmoothingStyle(fontId),
      } as CSSProperties)
    : {};

  const surface = enforceSurfaceTextContrast({
    ...defaultFunnelBaseStyle(!!resolvedTheme || !!palette || !!bgOverride),
    ...(resolvedTheme ? themeToStyle(resolvedTheme) : {}),
    ...(bgOverride ? { background: backgroundToCss(bgOverride) } : {}),
    ...(palette ? paletteToStyle(palette) : {}),
    // WEB-1009: палитра — слой-победитель, и пару «фон/текст» она обязана
    // переопределять ЦЕЛИКОМ. Её textPrimary до сих пор красил текст всегда,
    // а её background жил только в переменной --color-bg-primary: страницу
    // продолжал красить ключ `background` от темы (themeToStyle). Отсюда
    // «фон из одного слоя, текст из другого». Явный фон воронки по-прежнему
    // сильнее — бэк его снимает сам, когда палитра задаёт свой
    // (settings-operations.ts, applySetPalette).
    ...(palette?.background && !bgOverride
      ? { background: palette.background }
      : {}),
    // WEB-1045: палитра БЕЗ background, когда страницу не красит ни тема, ни
    // оверрайд — фон не задавал ни один слой (defaultFunnelBaseStyle при
    // палитре отдаёт только радиусы). Даём детерминированный белый — тот же,
    // что показывал редактор. Тема (в т.ч. градиентная) и оверрайд сильнее:
    // при них фон уже детерминирован своим слоем, ветка не срабатывает.
    ...(palette && !palette.background && !bgOverride && !resolvedTheme
      ? ({
          background: PALETTE_PAGE_BACKGROUND_FALLBACK,
          '--color-bg-primary': PALETTE_PAGE_BACKGROUND_FALLBACK,
        } as CSSProperties)
      : {}),
    // WEB-942: оверрайд воронки — ПОСЛЕ палитры (см. док функции).
    ...(textColor
      ? ({
          color: textColor,
          '--color-text-primary': textColor,
        } as CSSProperties)
      : {}),
    // Стиль-пак идёт ПОСЛЕ темы/палитры намеренно: форма (радиусы, плотность,
    // типографская шкала) — зона пака, тема оставляет за собой цвет. Пак не
    // эмитит ни одного цвета (guard в stylePackToStyle.test.ts), поэтому
    // перекрыть палитру он не может.
    ...(stylePack ? stylePackToStyle(stylePack) : {}),
    // Своё боковое поле экрана воронки — ПОСЛЕ пака: перекрывает его
    // `--screen-pad-x-mobile` (задание stylepack/screen-padding). Нет поля —
    // ни одного ключа, стиль прежний.
    ...(ownPadX === undefined
      ? {}
      : { [PAD_VAR]: ownPadX + 'px', [PAD_VAR + '-inner']: '0px' }),
    ...fontStyle,
  });

  // WEB-1710: переменные-двойники образцов палитры (`--rich-*`) объявляются
  // ЗДЕСЬ — на корне воронки, там же, где лежат сами переменные темы.
  //
  // Кусок текста, покрашенный образцом, читает двойник, а не переменную темы:
  // обёртка блока («Цвет текста внутри элемента») перекрывает на своё поддерево
  // именно переменные темы, и раньше ручная покраска слетала вместе с ними.
  // Двойник — ССЫЛКА на переменную темы, подстановка идёт на этом же элементе,
  // поэтому смена темы воронки по-прежнему ведёт покрашенный кусок за собой.
  //
  // Вне стража контраста намеренно: страж считает пару «фон/текст» по
  // конкретным ключам, ссылочные двойники в этот счёт попадать не должны.
  // Починенный им цвет двойник подхватывает сам — ссылка резолвится браузером.
  return { ...surface, ...richColorAliasStyle() } as CSSProperties;
}
